import { AppContext } from 'App.context';
import Colors from 'constants/Colors';
import { useRef, useContext, useEffect, useState } from 'react';
import { ApiContext } from 'utils/networking/Api.context';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import { saveAs } from 'file-saver';
import { getParsedReports } from '../../civitfun-police-report-utils/PoliceReportParse.utils';
import { capitalize } from 'utils/TextUtils';
import FIELDS from '../../civitfun-police-report-utils/PoliceReportFields';
import constants from 'utils/constants';

export default function usePoliceReportListHook() {
  const {
    appState,
    appActions: { getSelectedHotel, translate, setPoliceReportFilters, getSelectedLanguage },
  } = useContext(AppContext);
  const navigate = useNavigate();

  const { API } = useContext(ApiContext);

  const savedPaging = appState.policeReportFilters?.dataSource;
  const timeoutRef = useRef(null);
  const [state, setState] = useState({
    isLoading: false,
    dataSource: {
      items: [],
      totalItems: savedPaging?.totalItems || 0,
      totalPages: savedPaging?.totalPages || 0,
      page: savedPaging?.page || 0,
      size: savedPaging?.size || 10,
      reportStatus: [],
    },
    date: appState.policeReportFilters?.date || moment().subtract(1, 'days'),
    filters: appState.policeReportFilters?.filters || null,
    datesRange: getDefaultDateRange(),
    enableFilters: true,
    initialLoad: true,
    suscription: null,
    sendSuscriptionRequestEnabled: true,
    isLoadingForcePoliceReport: false,
    isForcedPoliceReport: false,
    host: false,
    username: false,
    password: false,
    port: false,
    folder: false,
    fileFormat: false,
    fileSufix: false,
    extension: false,
    ftpErrors: false,
    alertMessage: false,
    lang: 'es',
    platform: null,
    showAllDayDeleteWarning: false,
    statusTable: [],
    InterfaceType: null,
    statusSendReport: null,
    sendTimestamp: null,
    fileSearchError: null,
    isReportLoading: false,
    hasErtzFileConfig: false,
  });

  useEffect(() => {
    actions.loadPoliceReportConfig();
    if (state.platform) {
      actions.getPoliceReports();
    }
  }, [state.dataSource.page, state.filters, appState.selectedHotel, state.date, state.platform]);

  useEffect(() => {
    if (!state?.platform) {
      actions.getPlatform();
    }
    updateState({ lang: getSelectedLanguage().id });
  }, [appState.currentLanguage, state.platform, state.dataSource]);

  useEffect(() => {
    if (state.isReportLoading) {
      updateState({
        isLoading: true,
        dataSource: { ...state.dataSource, items: [] },
      });
      actions.retryGetAfterTimeout();
    } else {
      updateState({ isLoading: false });

      if (timeoutRef.current) {
        // Remove pending timeouts (if any)
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [state.isReportLoading]);

  const actions = {
    getAlerts: () => {
      API.getActiveDashboardAlerts()
        .then(res => {
          if (res && res.length > 0) {
            const alerta = res[0];
            const lang = getSelectedLanguage();
            updateState({ isLoading: false, alertMessage: alerta || null, lang: lang.id });
          }
        })
        .catch(err => {
          console.warn(err);
          updateState({ isLoading: false });
        });
    },

    loadPoliceReportConfig: () => {
      updateState({ isLoading: true });
      let InterfaceType;
      API.getPoliceConfig({ hotelId: getSelectedHotel()?.key })
        .then(res => {
          const format = 'YYYY-MM-DD';
          const file = res?.fileFormat || '';
          const extension = file ? file.substring(file.lastIndexOf('.') + 1) : '';
          const filePrefix = file.substring(0, file.indexOf(format));
          const fileWithoutExtension = file.replace(extension, '');
          const fileSufix = fileWithoutExtension.substring(
            fileWithoutExtension.indexOf(format) + format.length,
            fileWithoutExtension.length
          );

          switch (res?.bookingProvider) {
            case constants.BOOKING_PROVIDER.SFTP:
              InterfaceType = 'SFTP';
              break;
            case constants.BOOKING_PROVIDER.S3:
              InterfaceType = 'Civitfun SFTP';
              break;
            case constants.BOOKING_PROVIDER.API:
            default:
              InterfaceType = 'PMS';
              break;
          }

          updateState({
            suscription: res?.suscription === 'active',
            host: res?.ftpHost,
            username: res?.ftpUser,
            password: res?.ftpPass,
            port: res?.ftpPort,
            fileFormat: '',
            filePrefix: filePrefix,
            fileSufix: fileSufix,
            folder: res?.ftpDirPath,
            extension: extension,
            InterfaceType: InterfaceType,
            hasErtzFileConfig: !!res?.awsKey, // convert to boolean
          });
        })
        .catch(err => {
          console.warn(err);
          updateState({ isLoading: false });
        });
    },

    getPoliceReports: () => {
      // TODO REMOVE after we get credential and finished demo
      if (state.platform === 'alloggiati') {
        const parsedReports = getParsedReports(JSON.parse(JSON.stringify(mock)));
        updateState({ isLoading: false, dataSource: parsedReports });
        return;
      }

      const hotel = getSelectedHotel();

      API.registerDashboardAction('police_report', 'get_police_reports', {
        hotelId: hotel.id || hotel.key,
        date: moment(state.date).format('YYYY-MM-DD'),
        page: state.dataSource.page,
      });

      API.getPoliceReportList({
        hotelId: hotel.id || hotel.key,
        date: moment(state.date).format('YYYY-MM-DD'),
        page: state.dataSource.page,
        size: state.dataSource.size,
        status: state.filters?.status,
        reference: state.filters?.reference,
        guestName: state.filters?.guestName,
        batch: state.filters?.batch,
      })
        .then(res => {
          actions.setReportStatus(res);
          const parsedReports = getParsedReports(res);
          updateState({ isLoading: state.isReportLoading, dataSource: parsedReports });
        })
        .catch(err => {
          updateState({ isLoading: false });
        });
    },

    validateFileExport: () => {
      if (!state.host) {
        actions.forcePoliceReportDownloadTrigger();
        return;
      }

      updateState({ isLoading: true });
      API.policeReportValidateFileExport({
        hotelId: getSelectedHotel()?.key,
        host: state.host,
        username: state.username,
        password: state.password,
        port: parseInt(state.port),
        folder: state.folder,
        platform: 'SES',
        file:
          state.filePrefix +
          moment(state.date).format('YYYY-MM-DD') +
          state.fileSufix +
          state.extension,
      })
        .then(res => {
          if (res.length > 0) {
            const errorItems = res.filter(item => item.error === 'error');
            if (errorItems.length > 0) {
              const sortedValidations = [...errorItems];
              updateState({
                isLoading: false,
                ftpErrors: sortedValidations,
                ftpResult: res && res.length > 0 ? res : [],
                isValidation: true,
              });
              return;
            }
          }
          actions.forcePoliceReportDownloadTrigger();
          updateState({ isLoading: false, ftpErrors: [], ftpResult: [], isValidation: true });
          return;
        })
        .catch(err => {
          console.warn(err);
          updateState({
            isLoading: false,
            ftpErrors: [],
            ftpResult: [],
            isValidation: true,
          });
          notification.error({
            message: translate('policeReport.fileNotFound'),
          });
        });
    },

    requestGuestlinkFunctionality: () => {
      updateState({ isLoading: true });
      API.requestFunctionalityContract({
        hotelId: getSelectedHotel().key,
        suscription: 'police_report',
        functionality: 'Police Report Module',
      })
        .then(_ => {
          actions.sendRequestPoliceFunctionalityEmail();
        })
        .catch(_ => {
          updateState({ isLoading: false });
        });
    },

    sendRequestPoliceFunctionalityEmail: () => {
      updateState({ isLoading: true });
      API.sendRequestFuncionalityEmail({
        hotelId: getSelectedHotel().key,
        functionality: 'Police Report Module',
      })
        .then(_ => {
          notification.success({
            message: translate('policeReport.missingSuscription.requestSent'),
          });
          updateState({ isLoading: false, sendSuscriptionRequestEnabled: false });
        })
        .catch(_ => {
          updateState({ isLoading: false });
        });
    },

    forcePoliceReportDownloadTrigger: async () => {
      // If we have reports in this day, cant force the download
      if (state.dataSource?.totalItems > 0) {
        notification.warning({
          message: translate('policeReport.forceDownloadTriggerWarning'),
        });
        return;
      }

      const selectedHotel = getSelectedHotel();
      const hotelId = selectedHotel?.id ?? selectedHotel?.key;
      const date = moment(state.date).format('YYYY-MM-DD');
      updateState({ isLoadingForcePoliceReport: true, isReportLoading: true });
      try {
        // Register action
        API.registerDashboardAction('police_report', 'force_download_trigger', { hotelId, date });

        await API.forcePoliceReportDownloadTrigger({ hotelId, date });
        notification.success({
          message: translate('policeReport.forceDownloadTriggerInfo'),
        });
        updateState({ isForcedPoliceReport: true });
      } catch (error) {
        notification.error({
          message: translate('policeReport.forceDownloadTriggerError'),
        });
      }
      updateState({ isLoadingForcePoliceReport: false });
    },

    getColorForStatus: status => {
      switch (status) {
        case 'ok':
          return Colors.testOk;
        case 'error':
          return Colors.red;
        case 'pending':
          return Colors.yellowPill;
        default:
          return Colors.lightGray;
      }
    },

    onDateChange: (_date, dateString) => {
      updateState({ datesRange: dateString });
    },

    onDateSet: date => {
      updateState({ date, isForcedPoliceReport: false });
    },

    onRowClicked: data => {
      const platform = state.platform;
      data.platform = platform || ''; // set platform for guest details
      navigate(`/${platform.toLowerCase()}/police-report/PV/${data.id}`, {
        state: { report: data },
      });
      setPoliceReportFilters(state);
    },

    onChange: filters => {
      var newPage,
        newPageSize = -1;
      var searchFilters;

      if (state.dataSource?.page !== filters?.pagination.page) {
        newPage = filters?.pagination.page;
      }
      if (state.dataSource?.size !== filters?.pagination.pageSize) {
        newPageSize = filters?.pagination.pageSize;
      }

      if (filters?.where) {
        const reference = filters?.where['reference'];
        const guestName = filters?.where['surname'] || filters?.where['name'];
        const status = filters?.where['status'];
        const batch = filters?.where['batch'];

        searchFilters = { status, reference, guestName, batch };
      }

      updateState({
        dataSource: {
          ...state.dataSource,
          page: newPage >= 0 ? newPage : state.dataSource?.page,
          size: newPageSize >= 0 ? newPageSize : state.dataSource?.size,
          items: [],
        },
        filters: searchFilters,
      });
    },

    parseError: (error, object) => {
      if (error === undefined && !object.batch) {
        return translate('policeReport.erronInTheReport');
      } else if (error === undefined && object.batch) {
        return translate('policeReport.errorOnTargetPlatform');
      }

      try {
        let newString = error;
        newString = newString.replace(
          'Falta el campo requerido:',
          translate('policeReport.detailFields.requiredFields')
        );
        newString = newString.replace(
          'Error de validación del campo:',
          translate('policeReport.detailFields.fieldValidation')
        );

        const keysToReplace = Object.values(FIELDS);

        keysToReplace.forEach(key => {
          const regex = new RegExp(`\\b${key}\\b`, 'g');
          const translation = translate(`policeReport.detailFields.${key}`);
          newString = newString.replace(regex, translation);
        });

        return newString;
      } catch (errorMessage) {
        console.log('Error parsing message');
        return error;
      }
    },

    downloadPoliceReports: () => {
      updateState({ isLoading: true });
      API.downloadPoliceReports()
        .then(res => {
          saveAs(
            new Blob([res], {
              type: 'application/xml',
            }),
            'police_report_export.xml'
          );
          notification.success({
            message: translate('policeReport.downloadSuccess'),
          });
          updateState({ isLoading: false });
        })
        .catch(err => {
          notification.error({
            message: translate('policeReport.downloadError'),
          });
          updateState({ isLoading: false });
        });
    },
    getPlatform: () => {
      const hotelId = getSelectedHotel()?.key;

      API.getPoliceConfig({ hotelId }).then(res => {
        updateState({ platform: res.platform });
      });
    },
    sendGetFiles: () => {
      updateState({ isLoading: true });

      const date = moment(state.date).format('YYYY-MM-DD');
      const hotelId = getSelectedHotel()?.key;

      API.getDownloadFiles({ hotelId: hotelId, date: date })
        .then(res => {
          if (res && res.file && res.file.data) {
            const fileBuffer = res.file.data;

            const byteArray = new Uint8Array(fileBuffer);
            const blob = new Blob([byteArray], { type: 'application/zip' });

            saveAs(blob, res.fileName);
          } else {
            notification.error({
              message: translate('policeReport.downloadError'),
            });
          }
        })
        .catch(err => {
          notification.error({
            message: translate('policeReport.downloadError'),
          });
          updateState({ isLoading: false });
        });

      updateState({ isLoading: false });
    },

    sendAllErrors: () => {
      updateState({ isLoading: true });

      const body = {
        date: moment(state.date).format('YYYY-MM-DD'),
        hotelId: getSelectedHotel()?.key,
      };
      API.policeReportSendAllErrors(body)
        .then(res => {
          setTimeout(() => {
            notification.success({
              message: translate('policeReport.sendAllErrorsSuccess'),
            });

            // Reload bookings to show results
            updateState({ dataSource: { isLoading: false, ...state.dataSource, items: [] } });
            actions.getPoliceReports();
          }, 5000); // 5 second delay to show results.
        })
        .catch(err => {
          updateState({ isLoading: false });
          notification.error({
            message: translate('policeReport.sendAllErrorsError'),
          });
        });
    },
    onDeleteAllDayClicked: () => {
      actions.setShowDeleteAllWarning(true);
    },
    setShowDeleteAllWarning: show => {
      updateState({ showAllDayDeleteWarning: show });
    },
    sendDeleteAll: () => {
      updateState({ isLoading: true });

      const data = {
        allDay: true,
        date: moment(state.date).format('YYYY-MM-DD'),
        hotelId: getSelectedHotel().key,
      };

      API.policeReportSendDelete(data)

        .then(res => {
          setTimeout(() => {
            notification.success({
              message: translate('policeReport.delete.success'),
            });

            // Reload bookings to show results
            updateState({
              dataSource: {
                isLoading: false,
                ...state.dataSource,
                items: [],
                showAllDayDeleteWarning: false,
              },
            });
            actions.getPoliceReports();
          }, 1000); // 5 second delay to show results.

          updateState({
            isLoading: false,
            showAllDayDeleteWarning: false,
          });
        })
        .catch(err => {
          notification.error({
            message: translate('policeReport.delete.error'),
          });
          updateState({ isLoading: false, showGuestDeshowAllDayDeleteWarningleteWarning: false });
        });
    },

    setReportStatus: response => {
      const items = response?.items;
      const reportStatus = response?.reportStatus;
      const sendTimestamp = response?.reportStatus?.timestamp;
      const fileSearchError = response?.reportStatus?.ErrorMessage;
      const isReportLoading = response?.reportStatus?.isDashboardLoading || false;
      let status = 'notSent'; // Default value

      // If no status on report but there ara bookings set Ok
      if (!reportStatus?.length && items?.length > 0) {
        status = 'ok';
      }

      if (reportStatus?.status) {
        status = reportStatus.status;
      }

      updateState({
        statusSendReport: status,
        sendTimestamp: sendTimestamp,
        fileSearchError: fileSearchError,
        isReportLoading: isReportLoading,
      });
    },

    retryGetAfterTimeout: () => {
      updateState({ isLoading: true });

      // Guardamos la referencia del timeout
      timeoutRef.current = setTimeout(() => {
        actions.getPoliceReports();

        if (state.dataSource.items.length === 0 && state.isReportLoading) {
          actions.retryGetAfterTimeout();
        }
      }, 20000);
    },
  };

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  return { state, actions };
}

function getDefaultDateRange() {
  return [moment().subtract(1, 'day').startOf('day'), moment().subtract(0, 'day').endOf('day')];
}
const mock = {
  totalItems: 4, // ahora son 4 filas: 1 + 1 + 2 huéspedes
  totalPages: 1,
  page: 0,
  size: 10,
  items: [
    // ========= RESERVA 1 — OK =========
    {
      id: '11111111-1111-1111-1111-111111111111',
      report: {
        status: 'ok',
        message: 'booking confirmed',
      },
      guest: {
        holder: true,
        name: 'Luca',
        surname: 'Bianchi',
        secondSurname: '',
        gender: 'M',
        birthDate: '1985-06-10',
        documentType: 'PASOR',
        documentNumber: 'YA1234567',
        customFields: {
          email: 'luca.bianchi@example.com',
          document_support_number: 'S123456',
          country: 'ITA',
          municipality: 'Abano Terme', // country !== 'ESP' -> municipality (texto)
          zipCode: '35100',
          address: 'Via Roma 12',
          relationship: 'VI',
        },
      },
      payment: { type: 'CREDIT' },
      contract: {
        id: 'BOOK-IT-0001',
        reference: '504767515',
        contractDate: '2026-01-25',
        entrance: '2026-01-27',
        departure: '2026-01-30',
        paxes: 2,
        children: 0,
      },
    },

    // ========= RESERVA 2 — ERROR VALIDACIÓN (falta municipalityCode/zip/address en ESP) =========
    {
      id: '22222222-2222-2222-2222-222222222222',
      report: {
        status: 'error',
        message: 'validation failed',
      },
      guest: {
        holder: true,
        name: 'Sofía',
        surname: 'Martínez',
        secondSurname: 'López',
        gender: 'F',
        birthDate: '1990-03-22',
        documentType: 'DNI',
        documentNumber: '98765432L',
        error:
          'Falta el campo requerido: municipalityCode, address, zipCode.\r\nError de validación del campo: zipCode.',
        customFields: {
          phone: '+34 600 123 456', // sin email => contactType 'telefono'
          document_support_number: 'DOC-ES-001',
          country: 'ESP',
          municipalityCode: '', // ❌ vacío para forzar error
          address: '', // ❌
          zipCode: '', // ❌
          relationship: 'TI',
        },
      },
      payment: { type: 'TARJT' },
      contract: {
        id: 'BOOK-ES-0002',
        reference: '505155181',
        contractDate: '2026-01-20',
        entrance: '2026-01-27',
        departure: '2026-01-28',
        paxes: 1,
        children: 0,
      },
    },

    // ========= RESERVA 3 — HUESPED 1 (TITULAR) — ERROR VALIDACIÓN DOCUMENTO =========
    {
      id: '33333333-3333-3333-3333-333333333333',
      report: {
        status: 'error',
        message: 'validation failed',
      },
      guest: {
        holder: true, // titular => doc obligatorio
        name: 'Emma',
        surname: 'Johnson',
        secondSurname: '',
        gender: 'F',
        birthDate: '1988-11-05',
        documentType: '', // ❌ faltan documento/numero
        documentNumber: '', // ❌
        error: 'Falta el campo requerido: documentType, documentNumber, documentIssuanceCountry.',
        customFields: {
          email: 'emma.johnson@example.com',
          document_support_number: 'S-9876',
          country: 'USA',
          municipality: 'Beverly Hills',
          zipCode: '90210',
          address: '123 Elm Street',
          relationship: 'VI',
        },
      },
      payment: { type: 'PAYPAL' },
      contract: {
        id: 'BOOK-US-0003', // ⬅️ clave: misma reserva…
        reference: '507403672', // ⬅️ misma referencia…
        contractDate: '2026-01-18',
        entrance: '2026-01-27',
        departure: '2026-01-29',
        paxes: 2, // ⬅️ ahora establece 2 pax
        children: 0,
      },
    },

    // ========= RESERVA 3 — HUESPED 2 (NO TITULAR) — ERROR SUAVE DOCUMENTO =========
    {
      id: '44444444-4444-4444-4444-444444444444',
      report: {
        status: 'error',
        message: 'validation failed',
      },
      guest: {
        holder: false, // acompañante
        name: 'Olivia',
        surname: 'Williams',
        secondSurname: '',
        gender: 'F',
        birthDate: '1991-02-14',
        documentType: '',
        documentNumber: '',
        error: 'Falta el campo requerido: birthProvince, birthCity',
        customFields: {
          email: 'olivia.williams@example.com',
          document_support_number: 'S-2233',
          country: 'USA',
          municipality: 'Beverly Hills',
          zipCode: '90210',
          address: '123 Elm Street',
          relationship: 'VI',
        },
      },
      payment: { type: 'PAYPAL' },
      contract: {
        id: 'BOOK-US-0003', // ⬅️ misma reserva que la anterior
        reference: '507403672', // ⬅️ misma referencia
        contractDate: '2026-01-18',
        entrance: '2026-01-27',
        departure: '2026-01-29',
        paxes: 2,
        children: 0,
      },
    },
  ],
  statusReport: [],
};
